import React, { useEffect, useRef } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Container } from "react-bootstrap";
import TopNavbar from "../Components/TopNavbar.jsx";
import Footer from "../Components/Footer.jsx";
import { BLOGS, getPostBySlug } from "../data/blogData.js";
import "../Assets/Css/blog-post.css";

/* ─────────────────────────────────────────────────────────
   BLOCK RENDERERS
   Each type maps 1-to-1 with a block type in blogData.js
───────────────────────────────────────────────────────── */

const CheckIcon = () => (
  <svg viewBox="0 0 12 12" xmlns="http://www.w3.org/2000/svg">
    <polyline
      points="2,6 5,9 10,3"
      fill="none"
      stroke="#fff"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Paragraph = ({ block }) => (
  <p className="ik-post__p">{block.text}</p>
);

const Heading = ({ block }) => (
  <h2 className="ik-post__h2">
    {block.step != null && (
      <span className="ik-post__step-n">{block.step}</span>
    )}
    {block.text}
  </h2>
);

const Bullets = ({ block }) => (
  <ul className="ik-post__bullets">
    {block.items.map((item, i) => (
      <li key={i}>{item}</li>
    ))}
  </ul>
);

const BenefitList = ({ block }) => (
  <ul className="ik-post__benefit-list">
    {block.items.map(({ label, detail }, i) => (
      <li key={i} className="ik-post__benefit-item">
        <span className="ik-post__benefit-marker">
          <CheckIcon />
        </span>
        <span>
          <span className="ik-post__benefit-label">{label} </span>
          <span className="ik-post__benefit-detail">{detail}</span>
        </span>
      </li>
    ))}
  </ul>
);

/* Reality check — strong left bar, uppercase label, warm tint */
const Reality = ({ block }) => (
  <div className="ik-post__reality">
    <p>{block.text}</p>
  </div>
);

/* Tip block — sky-blue left bar, "HOW TO CHOOSE" label */
const Tip = ({ block }) => (
  <div className="ik-post__tip">
    <p>{block.text}</p>
  </div>
);

/* Innkeeper feature block — dark navy, grid of branded checks */
const InnkeeperBlock = ({ block }) => (
  <div className="ik-post__innkeeper">
    <div className="ik-post__innkeeper-inner">
      <p className="ik-post__innkeeper-eyebrow">Innkeeper Talent Accelerator</p>
      <h3 className="ik-post__innkeeper-title">{block.title}</h3>
      <p className="ik-post__innkeeper-body">{block.body}</p>
      <ul className="ik-post__innkeeper-checks">
        {block.checks.map((c, i) => (
          <li key={i}>{c}</li>
        ))}
      </ul>
    </div>
  </div>
);

/* Responsive card grid */
const CardGrid = ({ block }) => (
  <div className="ik-post__card-grid">
    {block.items.map(({ icon, title, desc }) => (
      <div key={title} className="ik-post__path-card">
        <span className="ik-post__path-card-icon">{icon}</span>
        <p className="ik-post__path-card-title">{title}</p>
        <p className="ik-post__path-card-desc">{desc}</p>
      </div>
    ))}
  </div>
);

/* Icon-numbered platform/channel rows */
const NumberedList = ({ block }) => (
  <ul className="ik-post__icon-list">
    {block.items.map(({ icon, title, desc }) => (
      <li key={title}>
        <div className="ik-post__icon-chip">{icon}</div>
        <div>
          <p className="ik-post__icon-list-title">{title}</p>
          <p className="ik-post__icon-list-desc">{desc}</p>
        </div>
      </li>
    ))}
  </ul>
);

/* Certification list */
const CertList = ({ block }) => (
  <ul className="ik-post__cert-list">
    {block.items.map(({ badge, badgeColor, label, desc }) => (
      <li key={badge} className="ik-post__cert-item">
        <span
          className="ik-post__cert-badge"
          style={{ background: badgeColor || "#00114c" }}
        >
          {badge}
        </span>
        <div>
          <p className="ik-post__cert-label">{label}</p>
          <p className="ik-post__cert-desc">{desc}</p>
        </div>
      </li>
    ))}
  </ul>
);

/* Programme table */
const ProgrammeTable = ({ block }) => (
  <div className="ik-post__table-wrap">
    <table className="ik-post__table">
      <thead>
        <tr>
          <th>Programme</th>
          <th>Duration</th>
          <th>Fee (₦)</th>
          <th>Mode</th>
          <th>Best For</th>
        </tr>
      </thead>
      <tbody>
        {block.rows.map(({ programme, duration, fee, mode, modeColor, bestFor }) => (
          <tr key={programme}>
            <td>{programme}</td>
            <td>{duration}</td>
            <td>{fee}</td>
            <td>
              <span
                className="ik-post__table-badge"
                style={{ background: modeColor || "#00114c" }}
              >
                {mode}
              </span>
            </td>
            <td>{bestFor}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/* CTA block */
const CtaBlock = ({ block }) => (
  <div className="ik-post__cta">
    <div className="ik-post__cta-inner">
      <p className="ik-post__cta-eyebrow">Ready to start?</p>
      <h2>{block.title}</h2>
      <p className="ik-post__cta-body">{block.body}</p>

      {/* Optional course pills */}
      {block.courses && block.courses.length > 0 && (
        <div className="ik-post__course-pills">
          {block.courses.map((c) => (
            <span key={c} className="ik-post__course-pill">{c}</span>
          ))}
        </div>
      )}

      <div className="ik-post__cta-actions">
        <a
          href={block.primaryHref}
          target="_blank"
          rel="noreferrer"
          className="ik-post__btn-primary"
        >
          {block.primaryLabel}
        </a>
        {block.secondaryLabel && (
          <a
            href={block.secondaryHref}
            target="_blank"
            rel="noreferrer"
            className="ik-post__btn-ghost"
          >
            {block.secondaryLabel}
          </a>
        )}
      </div>

      {block.note && (
        <p className="ik-post__cta-note">{block.note}</p>
      )}
    </div>
  </div>
);


/* ── Block dispatcher ── */
const renderBlock = (block, index) => {
  switch (block.type) {
    case "paragraph":     return <Paragraph    key={index} block={block} />;
    case "heading":       return <Heading      key={index} block={block} />;
    case "bullets":       return <Bullets      key={index} block={block} />;
    case "benefit-list":  return <BenefitList  key={index} block={block} />;
    case "reality":       return <Reality      key={index} block={block} />;
    case "tip":           return <Tip          key={index} block={block} />;
    case "innkeeper":     return <InnkeeperBlock key={index} block={block} />;
    case "card-grid":     return <CardGrid     key={index} block={block} />;
    case "numbered-list": return <NumberedList key={index} block={block} />;
    case "cert-list":     return <CertList     key={index} block={block} />;
    case "programme-table": return <ProgrammeTable  key={index} block={block} />;  
    case "cta":           return <CtaBlock     key={index} block={block} />;
    default:              return null;
  }
};
/* ─────────────────────────────────────────────────────────
   MiniCard — "More articles" rail
   Shows coverImage if available, falls back to coverGradient
───────────────────────────────────────────────────────── */
const MiniCard = ({ post }) => (
  <Link to={`/blog/${post.slug}`} className="ik-blog-card" style={{ textDecoration: "none" }}>
    <div className="ik-blog-card__cover">
      {post.coverImage ? (
        <img
          src={post.coverImage}
          alt={post.title}
          className="ik-blog-card__cover-img"
        />
      ) : (
        <div
          className="ik-blog-card__cover-bg"
          style={{ background: post.coverGradient }}
        />
      )}
      <div className="ik-blog-card__cover-pattern" />
      <div className="ik-blog-card__cover-overlay" />
      <span className="ik-blog-card__category">{post.category}</span>
    </div>
    <div className="ik-blog-card__body">
      <div className="ik-blog-card__meta">
        <span>{post.readTime}</span>
      </div>
      <h3 className="ik-blog-card__title">{post.title}</h3>
      <span className="ik-blog-card__read">
        Read article
        <span className="ik-blog-card__read-arrow">→</span>
      </span>
    </div>
  </Link>
);

/* ─────────────────────────────────────────────────────────
   BlogPost — main page component
───────────────────────────────────────────────────────── */
const BlogPost = () => {
  const { slug } = useParams();
  const post = getPostBySlug(slug);
  const progressRef = useRef(null);

  /* Reading progress */
  useEffect(() => {
    const onScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const pct = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
      if (progressRef.current) {
        progressRef.current.style.width = `${Math.min(pct, 100)}%`;
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* Scroll to top on new post */
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  /* 404 */
  if (!post) return <Navigate to="/blog" replace />;

  /* Sibling posts for "More articles" */
  const morePosts = BLOGS.filter((b) => b.slug !== post.slug).slice(0, 2);

  return (
    <div className="ik-post">
      <TopNavbar />

      {/* Progress bar */}
      <div className="ik-post__progress">
        <div className="ik-post__progress-fill" ref={progressRef} />
      </div>

      {/* Hero */}
      <section
        className="ik-post__hero"
        style={{ background: post.coverGradient }}
      >
        {/* Hero background image — sits behind the dark overlay */}
        {post.coverImage && (
          <img
            src={post.coverImage}
            alt=""
            aria-hidden="true"
            className="ik-post__hero-img"
          />
        )}

        <Container>
          <div className="ik-post__hero-inner">
            <Link to="/blog" className="ik-post__back">
              ← All articles
            </Link>

            <div className="ik-post__meta-row">
              <span className="ik-post__category-pill">{post.category}</span>
              <div className="ik-post__meta-text">
                <span>⏱ {post.readTime}</span>
                <span>·</span>
                <span>
                  {new Date(post.date).toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                </span>
              </div>
            </div>

            <h1 className="ik-post__title">{post.title}</h1>

            {post.quote && (
              <blockquote className="ik-post__quote">
                <p>"{post.quote.text}"</p>
                <cite>— {post.quote.author}</cite>
              </blockquote>
            )}
          </div>
        </Container>
      </section>

      {/* Article body */}
      <section className="ik-post__body">
        <Container>
          <article className="ik-post__article">
            {post.content.map((block, i) => renderBlock(block, i))}
          </article>
        </Container>
      </section>

      {/* More articles */}
      {morePosts.length > 0 && (
        <section className="ik-post__more">
          <Container>
            <p className="ik-post__more-label">More from Innkeeper Insights</p>
            <div className="ik-post__more-grid">
              {morePosts.map((p) => (
                <MiniCard key={p.id} post={p} />
              ))}
            </div>
          </Container>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default BlogPost;